import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';
import {
  Box,
  Grid,
  Button,
  SelectChangeEvent,
  Select,
  ListSubheader,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {UploadFile, ClearAll} from '@mui/icons-material';
import {useSnackbar} from 'notistack';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {useAccountContext} from '../../contexts/AccountContext';
import {
  notesField,
  planNumberField,
  providerField,
  taxYearField,
  datePickerField,
  contactNameField,
  planNumberStatusField,
} from './FileUploadFormFieldsV2';
import {useClientFilesV2} from './useClientFilesV2';
import {toBase64} from './utilsV2';
import {
  useDocumentUploaderFormConfigs,
  ValueStringLabel,
} from '../../hooks/useDocumentUploaderFormConfigs';
import {FileUploadFormValues, initialFileUploadFormValues} from './typesV2';
import Loading from '../../components/Loading/Loading';
import {documentUploaderFormConfigs_documentUploaderFormConfigs} from '../../schema-types/documentUploaderFormConfigs';
import usePlanNamesProviders from '../../hooks/usePlanNamesProviders';
import {getTaxYearDropdownOptions, getTaxYearFromEndYear} from '../../utils/getTaxYear';
import {UploadClientDocumentV2Variables} from '../../schema-types/UploadClientDocumentV2';
import {
  DocumentGroup,
  DocumentUploadPlanNumberStatus,
} from '../../schema-types/globalTypes';
import {UPDATES} from '../../shared/UPDATES';
import validateUUID from '../../utils/validateUUID';
import {UUID} from '../../graphql/uuid';

const sortAlphabetically = (a: ValueStringLabel, b: ValueStringLabel) => {
  const aName = a.label?.toLowerCase() || '';
  const bName = b.label?.toLowerCase() || '';
  if (aName < bName) {
    return -1;
  }
  if (aName > bName) {
    return 1;
  }
  return 0;
};

const FileUploadForm = () => {
  const {values, resetForm, setFieldValue, setValues, errors} =
    useAutoForm<FileUploadFormValues>();
  const {account, pots} = useAccountContext();
  const {uploadClientFile, uploadClientFileLoading} = useClientFilesV2();
  const {enqueueSnackbar} = useSnackbar();
  const {formConfig, nestedCategories, loading, error} = useDocumentUploaderFormConfigs();
  const [currentConfig, setCurrentConfig] =
    useState<documentUploaderFormConfigs_documentUploaderFormConfigs | null>(null);

  useEffect(() => {
    if (
      !Object.keys(formConfig).length ||
      values.documentGroup === currentConfig?.enumName
    ) {
      return;
    }
    setCurrentConfig(values.documentGroup ? formConfig[values.documentGroup] : null);
  }, [values.documentGroup, formConfig, currentConfig]);

  useEffect(() => {
    if (!values.contactName1) {
      setFieldValue(UPDATES._1400, 'contactName2', undefined);
    }
  }, [values.contactName1]);

  useEffect(() => {
    // datepicker is acting strange and populating the field with today's date instead of blank
    if (!values.datePicker && values.datePicker !== null) {
      setFieldValue(UPDATES._1401, 'datePicker', null);
    }
  }, [values.datePicker]);

  const handleFileEvent = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event?.target?.files;
    if (!files) {
      return;
    }

    const newFiles = Array.from(files);

    setFieldValue(UPDATES._1402, 'files', newFiles);
    // this prevents a bug where the same file cannot be selected twice
    event.target.value = '';
  };

  const taxYearOptions = getTaxYearDropdownOptions(new Date());

  const handleUpload = async () => {
    if (!values.files) {
      enqueueSnackbar('No files selected for upload.', {variant: 'warning'});
      return;
    }
    try {
      const file = values.files[0];
      const fileData = await toBase64(file);

      const uploadVariables: UploadClientDocumentV2Variables = {
        accountId: validateUUID(account?.curoId)!,
        contactId: validateUUID(values.contactId1),
        datePicked: values.datePicker,
        documentGroup:
          values.documentGroup ?? DocumentGroup.COMPLETED_FORMS_AUTHORITY_TO_PROCEED,
        fileData,
        notes: values.notes ?? '',
        planNumberStatus: values.planNumberStatus,
      };
      if (currentConfig?.shouldShowSecondContact) {
        uploadVariables.secondContactId = values.contactId2;
      }
      if (
        currentConfig?.shouldShowPlanNumber &&
        values.planNumberStatus ===
          DocumentUploadPlanNumberStatus.PLAN_NUMBER_NEEDED_KNOWN
      ) {
        uploadVariables.potId = values.planNumber;
      }
      if (currentConfig?.shouldShowProvider) {
        uploadVariables.providerId = values.providerId;
      }
      const selectedTaxYear = getTaxYearFromEndYear(
        taxYearOptions,
        Number(values.taxYear)
      );
      if (currentConfig?.shouldShowTaxYear && selectedTaxYear) {
        uploadVariables.taxYear = selectedTaxYear;
      }

      await uploadClientFile({
        variables: uploadVariables,
        onCompleted: (data) => {
          if (data?.clientDocumentUploadV2?.ok) {
            enqueueSnackbar('File uploaded successfully.', {variant: 'success'});
            handleClear();
          } else {
            enqueueSnackbar(
              `File upload failed: ${data?.clientDocumentUploadV2?.errors?.join(', ')}`,
              {variant: 'error'}
            );
          }
        },
        onError: (errorRes: Error) => {
          enqueueSnackbar(`File upload failed: ${errorRes.message}`, {variant: 'error'});
        },
      });
    } catch (errorRes) {
      enqueueSnackbar('File upload failed: An unknown error occurred', {
        variant: 'error',
      });
    }
  };

  const handleClear = () => {
    resetForm();
    setValues(UPDATES._1450, initialFileUploadFormValues);
    setCurrentConfig(null);
    setFieldValue(UPDATES._1402, 'files', []);
  };

  const availableContactOptions1 = [
    account?.primaryContact?.name && values.contactName2 !== account.primaryContact.name
      ? {
          value: account.primaryContact.name,
          label: account.primaryContact.name,
          id: account.primaryContact.id,
        }
      : null,
    account?.secondaryContact?.name &&
    values.contactName2 !== account.secondaryContact.name
      ? {
          value: account.secondaryContact.name,
          label: account.secondaryContact.name,
          id: account.secondaryContact.id,
        }
      : null,
  ].filter((option): option is {value: string; label: string; id: UUID} => !!option);

  const availableContactOptions2 = [
    account?.primaryContact?.name && values.contactName1 !== account.primaryContact.name
      ? {
          value: account.primaryContact.name,
          label: account.primaryContact.name,
          id: account.primaryContact.id,
        }
      : null,
    account?.secondaryContact?.name &&
    values.contactName1 !== account.secondaryContact.name
      ? {
          value: account.secondaryContact.name,
          label: account.secondaryContact.name,
          id: account.secondaryContact.id,
        }
      : null,
  ].filter((option): option is {value: string; label: string; id: UUID} => !!option);

  // Sync contactId1 when contactName1 changes
  useEffect(() => {
    const selectedContact1 = availableContactOptions1.find(
      (contact) => contact.value === values.contactName1
    );
    setFieldValue(UPDATES._1403, 'contactId1', selectedContact1?.id || '');
  }, [values.contactName1]);

  // Sync contactId2 when contactName2 changes
  useEffect(() => {
    const selectedContact2 = availableContactOptions2.find(
      (contact) => contact.value === values.contactName2
    );
    setFieldValue(UPDATES._1404, 'contactId2', selectedContact2?.id || undefined);
  }, [values.contactName2]);

  const {rawProviders, queryGetProductProviders} = usePlanNamesProviders();
  useEffect(() => {
    if (!rawProviders) {
      queryGetProductProviders({
        variables: {
          accountId: account?.id,
        },
      });
    }
  }, [rawProviders]);

  useEffect(() => {
    if (currentConfig?.shouldShowPlanNumber) {
      setFieldValue(
        UPDATES._1405,
        'planNumberStatus',
        DocumentUploadPlanNumberStatus.PLAN_NUMBER_NEEDED_KNOWN
      );
    } else {
      setFieldValue(
        UPDATES._1405,
        'planNumberStatus',
        DocumentUploadPlanNumberStatus.PLAN_NUMBER_NOT_NEEDED
      );
    }
  }, [currentConfig?.shouldShowPlanNumber]);

  const datePickerFieldMemoed = useMemo(() => {
    return datePickerField(currentConfig?.dateFieldLabel || 'No date');
  }, [datePickerField, currentConfig?.dateFieldLabel]);

  if (loading) {
    return <p>Loading...</p>;
  }
  if (error) {
    return <p>Error loading configurations: {error.message}</p>;
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Panel title="Document Upload Form" color={themeColors.chartPurple}>
          <Box sx={{padding: 4}}>
            {uploadClientFileLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <Loading />
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl
                    fullWidth
                    required
                    size="small"
                    error={!!errors.documentGroup}
                  >
                    <InputLabel>Document Type</InputLabel>
                    <Select
                      label="Document Type" //  this redudant label helps MUI minimise the label when an input is chosen
                      fullWidth
                      onChange={(e: SelectChangeEvent<any>) => {
                        setFieldValue(UPDATES._1406, 'documentGroup', e.target.value);
                      }}
                      value={values.documentGroup || ''}
                      defaultValue=""
                      renderValue={(value) => {
                        const thisConfig = formConfig[value];
                        return `${thisConfig.category} - ${thisConfig.subcategory}`;
                      }}
                    >
                      {Object.keys(nestedCategories).map((key) => {
                        return [
                          <ListSubheader
                            key={key}
                            sx={{
                              fontWeight: 'bold',
                              fontStyle: 'italic',
                            }}
                          >
                            {key}
                          </ListSubheader>,
                          ...nestedCategories[key]
                            .sort((a, b) => {
                              if (a.label < b.label) {
                                return -1;
                              }
                              if (a.label > b.label) {
                                return 1;
                              }
                              return 0;
                            })
                            .map((item) => {
                              return (
                                <MenuItem value={item.value} key={item.value}>
                                  {item.label}
                                </MenuItem>
                              );
                            }),
                        ];
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <AutoField field={datePickerFieldMemoed} />
                </Grid>
                <Grid item xs={6}>
                  <AutoField
                    field={contactNameField(
                      'contactName1',
                      [...availableContactOptions1].filter(
                        (option) => option.value !== values.contactName2
                      )
                    )}
                  />
                </Grid>
                {currentConfig?.shouldShowSecondContact && account?.secondaryContact && (
                  <Grid item xs={6}>
                    <AutoField
                      field={contactNameField('contactName2', [
                        {
                          value: '',
                          label: 'No contact',
                        },
                        ...[...availableContactOptions2].filter(
                          (option) => option.value !== values.contactName1
                        ),
                      ])}
                      disabled={!values.contactName1}
                    />
                  </Grid>
                )}
                {currentConfig?.shouldShowProvider && (
                  <Grid item xs={6}>
                    <AutoField
                      field={{
                        ...providerField,
                        label: currentConfig.providerFieldLabel,
                        options: (rawProviders?.productProviders || [])
                          .map((x) => {
                            return {
                              value: x.id,
                              label: x.name ?? 'N/A',
                            };
                          })
                          .sort(sortAlphabetically),
                      }}
                    />
                  </Grid>
                )}
                {currentConfig?.shouldShowPlanNumber && (
                  <Grid item xs={6}>
                    <AutoField
                      field={planNumberField(pots)}
                      disabled={
                        values.planNumberStatus ===
                        DocumentUploadPlanNumberStatus.PLAN_NUMBER_NEEDED_NOT_KNOWN
                      }
                    />
                    <AutoField field={planNumberStatusField} />
                  </Grid>
                )}
                {currentConfig?.shouldShowTaxYear && (
                  <Grid item xs={6}>
                    <AutoField
                      field={{
                        ...taxYearField,
                        options: getTaxYearDropdownOptions(new Date()).map((taxYear) => {
                          const yearStr = `${String(taxYear.startYear).slice(
                            -2
                          )}-${String(taxYear.endYear).slice(-2)}`;
                          return {
                            value: taxYear.endYear ?? 0,
                            label: yearStr,
                          };
                        }),
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <AutoField field={notesField} data-test="upload notes" />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleClear}
                      startIcon={<ClearAll />}
                      sx={{mr: 2}}
                    >
                      Clear All
                    </Button>
                    <Button
                      component="label"
                      variant="contained"
                      sx={{mr: 2}}
                      data-test="select file"
                    >
                      {values.files?.length ? 'Change selected file' : 'Select file'}
                      <input
                        hidden
                        type="file"
                        accept="application/pdf"
                        onChange={handleFileEvent}
                        data-testid="file-input"
                      />
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpload}
                      startIcon={<UploadFile />}
                      disabled={!!Object.keys(errors).length}
                      data-test="upload file"
                    >
                      Upload
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </Panel>
      </Grid>
    </Grid>
  );
};

export const FileUploadFormWrapped = withAutoFormNew<FileUploadFormValues, unknown>(
  FileUploadForm,
  undefined,
  {debugPosition: 'right'}
);
